<template>
  <div class="irp-partner-filter-bar" data-testid="irp-partner-filter-bar">
    <div class="irp-partner-filter-bar__header">
      <h2 class="irp-partner-filter-bar__heading">Partner data</h2>
      <BittsButton
        text="Report incorrect match"
        :left-icon="['fak', 'warning']"
        type="neutral"
        variant="outline"
        size="small"
        :disabled="!matchIssuePartners.length"
        data-testid="irp-partner-filter-bar-report-match"
        @click="openMatchIssueModal"
      />
    </div>
    <div class="irp-partner-filter-bar__controls">
      <BittsQuickFilter
        v-model="partnerSelection"
        :filter="PARTNER_FILTER"
        :options="partnerOptions"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-partner"
        @apply-filter="applyPartnerFilter"
        @clear-filter="clearPartnerFilter"
        @remove-filter="(reload) => reload && clearPartnerFilter()"
      >
        <template #option-prefix-icon="{ item }">
          <BittsAvatar :org="orgForValue(item.value)" size="x-small" />
        </template>
        <template #option-suffix-icon="{ item }">
          <OfflineManagedPartnerLabel
            v-if="isOfflineOrVirtualValue(item.value)"
            :mount-to-body="true"
            :partner="orgForValue(item.value) as PartnerTrimmed"
            :is-tag="false"
            placement="right"
          />
        </template>
      </BittsQuickFilter>
      <BittsQuickFilter
        v-model="segmentSelection"
        :filter="SEGMENT_FILTER"
        :options="SEGMENT_OPTIONS"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-population"
        @apply-filter="applySegmentFilter"
        @clear-filter="clearSegmentFilter"
        @remove-filter="(reload) => reload && clearSegmentFilter()"
      />
      <BittsQuickFilter
        v-model="scoreSelection"
        :filter="SCORE_FILTER"
        :options="SCORE_BUCKET_OPTIONS"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-score"
        @apply-filter="applyScoreFilter"
        @clear-filter="clearScoreFilter"
        @remove-filter="(reload) => reload && clearScoreFilter()"
      />
      <BittsQuickFilter
        v-model="tagSelection"
        :filter="TAGS_FILTER"
        :options="tagOptions"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-tags"
        @apply-filter="applyTagFilter"
        @clear-filter="clearTagFilter"
        @remove-filter="(reload) => reload && clearTagFilter()"
      />
      <IRPExpandToggle
        :is-expanded="isExpanded"
        :cta="IRP_CTAS.PARTNER_ROW"
        data-testid="irp-partner-filter-bar-expand-toggle"
        @update:is-expanded="(value) => emit('toggle-expansion', value)"
      />
    </div>
    <ReportMatchIssueModal
      v-if="isMatchIssueModalVisible && sourceId && sourcePrimaryKey"
      :partners="matchIssuePartners"
      :record-name="recordName"
      :record-domain="recordDomain"
      :mdm-type="mdmType"
      :source-id="sourceId"
      :source-primary-key="sourcePrimaryKey"
      @closed="isMatchIssueModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsButton, BittsQuickFilter } from '@crossbeam/bitts';
import type { BittsFilterProp } from '@crossbeam/bitts';
import { IRP_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import OfflineManagedPartnerLabel from '@/components/partners/OfflineManagedPartnerLabel.vue';
import ReportMatchIssueModal from '@/components/records/ReportMatchIssueModal.vue';

import { MDM_PROPERTIES } from '@/constants/mdm';
import { useIndividualRecordStore, usePartnersStore } from '@/stores';
import type { RecordFiltersType } from '@/types/individual_record';
import { isManagedOfflinePartner, isOfflinePartner } from '@/types/partners';
import type {
  Partner,
  PartnerScoreBucket,
  PartnerTag,
  PartnerTrimmed,
} from '@/types/partners';
import { ALL_SEGMENTS, SEGMENTS_TO_LABEL_MAP } from '@/types/populations';
import type { SegmentType } from '@/types/populations';
import { websiteDomain } from '@/utils/websiteDomain';

import IRPExpandToggle from './IRPExpandToggle.vue';
import {
  PARTNER_SCORE_BUCKETS,
  PARTNER_SCORE_BUCKET_LABELS,
  filtersFromQuery,
  filtersToQuery,
} from './recordFilterQuery';
import { useIRPTracking } from './useIRPTracking';

const { isExpanded, matchedPartnerUuids } = defineProps<{
  isExpanded: boolean;
  matchedPartnerUuids: string[];
}>();

const emit = defineEmits<{
  'toggle-expansion': [isExpanded: boolean];
}>();

const PARTNER_FILTER: BittsFilterProp = {
  label: 'Partner',
  value: 'partner',
  faIcon: ['fak', 'partner'],
  datatype: 'text',
  useSearch: true,
};

const SEGMENT_FILTER: BittsFilterProp = {
  label: 'Populations',
  value: 'populations',
  faIcon: ['fak', 'population'],
  datatype: 'text',
  useSearch: true,
};

const SCORE_FILTER: BittsFilterProp = {
  label: 'Score',
  value: 'partner_score',
  faIcon: ['fak', 'partner-score'],
  datatype: 'text',
  useSearch: false,
};

const TAGS_FILTER: BittsFilterProp = {
  label: 'Tags',
  value: 'partner_tags',
  faIcon: ['fak', 'tag'],
  datatype: 'text',
  useSearch: true,
};

const SCORE_BUCKET_OPTIONS = PARTNER_SCORE_BUCKETS.map((bucket) => ({
  label: PARTNER_SCORE_BUCKET_LABELS[bucket],
  value: bucket,
}));

const SEGMENT_OPTIONS = ALL_SEGMENTS.map((segment) => ({
  label: SEGMENTS_TO_LABEL_MAP[segment],
  value: segment,
}));

const individualRecordStore = useIndividualRecordStore();
const { sourceId, sourcePrimaryKey, mdmType, recordName } = storeToRefs(
  individualRecordStore,
);
const partnersStore = usePartnersStore();
const { partnerOrgs, partnerTags } = storeToRefs(partnersStore);

const { trackIRPInteraction } = useIRPTracking();

function trackFilter(
  filterName: BittsFilterProp['value'],
  action: 'apply' | 'clear',
) {
  trackIRPInteraction({
    cta: IRP_CTAS.FILTER,
    cta_2: String(filterName),
    metadata: { action },
  });
}

const isMatchIssueModalVisible = ref(false);

function openMatchIssueModal() {
  isMatchIssueModalVisible.value = true;
  trackIRPInteraction({ cta: IRP_CTAS.REPORT_INCORRECT_MATCH });
}

const matchIssuePartners = computed(() => {
  const matchedUuids = new Set(matchedPartnerUuids);
  return partnerOrgs.value
    .filter(
      (org: Partner) =>
        matchedUuids.has(org.uuid) &&
        !isOfflinePartner(org) &&
        !isManagedOfflinePartner(org),
    )
    .map((org: Partner) => ({
      ...org,
      value: org.id,
      label: org.name,
    }));
});

const recordDomain = computed(() => {
  const websiteField = individualRecordStore.ownMasterSection.own?.fields?.find(
    (field) => field.mdm_property === MDM_PROPERTIES.account.WEBSITE,
  );
  return websiteDomain(websiteField?.value) || null;
});

const partnerOptions = computed(() =>
  partnerOrgs.value.map((org: Partner) => ({
    label: org.name,
    value: org.id,
  })),
);

function orgForValue(value: string | number) {
  return partnersStore.getPartnerOrgById(Number(value));
}

function isOfflineOrVirtualValue(value: string | number) {
  const org = orgForValue(value);
  return !!org && (org.offline_partner || org.virtual_partner);
}

const tagOptions = computed(() =>
  partnerTags.value
    .filter((tag: PartnerTag) => tag.label)
    .map((tag: PartnerTag) => ({
      label: tag.label,
      value: tag.id as string | number,
    })),
);

const route = useRoute();
const router = useRouter();

const initialFilters = filtersFromQuery(route.query);

function updateQuery(filters: Partial<RecordFiltersType>) {
  return router.replace({ query: filtersToQuery(route.query, filters) });
}

const partnerSelection = ref<(string | number)[]>(
  initialFilters.partner_org_ids,
);
function applyPartnerFilter() {
  trackFilter(PARTNER_FILTER.value, 'apply');
  return updateQuery({ partner_org_ids: partnerSelection.value.map(Number) });
}
function clearPartnerFilter() {
  partnerSelection.value = [];
  trackFilter(PARTNER_FILTER.value, 'clear');
  return updateQuery({ partner_org_ids: [] });
}

const segmentSelection = ref<(string | number)[]>(
  initialFilters.partner_segments,
);
function applySegmentFilter() {
  trackFilter(SEGMENT_FILTER.value, 'apply');
  return updateQuery({
    partner_segments: segmentSelection.value as SegmentType[],
  });
}
function clearSegmentFilter() {
  segmentSelection.value = [];
  trackFilter(SEGMENT_FILTER.value, 'clear');
  return updateQuery({ partner_segments: [] });
}

const scoreSelection = ref<(string | number)[]>(
  initialFilters.partner_score_bucket,
);
function applyScoreFilter() {
  trackFilter(SCORE_FILTER.value, 'apply');
  return updateQuery({
    partner_score_bucket:
      scoreSelection.value as NonNullable<PartnerScoreBucket>[],
  });
}
function clearScoreFilter() {
  scoreSelection.value = [];
  trackFilter(SCORE_FILTER.value, 'clear');
  return updateQuery({ partner_score_bucket: [] });
}

const tagSelection = ref<(string | number)[]>(initialFilters.partner_tag_ids);
function applyTagFilter() {
  trackFilter(TAGS_FILTER.value, 'apply');
  return updateQuery({ partner_tag_ids: tagSelection.value.map(String) });
}
function clearTagFilter() {
  tagSelection.value = [];
  trackFilter(TAGS_FILTER.value, 'clear');
  return updateQuery({ partner_tag_ids: [] });
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-partner-filter-bar {
  @apply flex flex-col gap-8;
}

.irp-partner-filter-bar__header {
  @apply flex flex-wrap items-center justify-between gap-8;
}

.irp-partner-filter-bar__heading {
  @apply m-0 text-m font-semibold text-neutral-text-strong;
}

.irp-partner-filter-bar__controls {
  @apply flex flex-wrap items-center gap-8;
}
</style>
